import React, { Component } from 'react';
import {
  Modal, ModalHeader, ModalBody, Button, Nav, NavItem, NavLink,
  TabContent, TabPane, ListGroup, ListGroupItem, FormGroup, Label, Input, Alert,
  InputGroup, InputGroupAddon, Spinner,
} from 'reactstrap';
import Select from 'react-select';
import 'react-select/dist/react-select.css';
import qs from 'query-string';
import { apiClient } from '../../utils/Api';
import { RAILS, railEndpoint, ENDPOINT_PREFIXES } from './registry';

const FIXED = {
  brand: ['Popular brands', '/brands/populars', 'brand'],
  slider: ['Slider set', '/sliders?category=all_category', 'slider'],
  shortcut: ['Shortcut config', '/settings?name=shortcut_app_kickavenue', 'shortcut'],
};

const CAT_OPTS = ['sneakers', 'apparels', 'handbags', 'lifestyles'].map((c) => ({ value: c, label: c }));
// Same list + an explicit "All categories" choice (no single-category filter).
// ALL_CAT doubles as the "omit the param" sentinel, which is safe HERE because /search does not
// accept it as a category: `category=all_category` matches nothing (verified against prod —
// 0 results, versus 41k with the param absent). So a hand-written endpoint carrying it is a dead
// section, and dropping the param on apply repairs it rather than losing a stored filter. The
// pseudo-category IS meaningful on other endpoints (/sliders, the /products rails), just not here.
const ALL_CAT = 'all_category';
const CAT_OPTS_WITH_ALL = [{ value: ALL_CAT, label: 'All categories' }].concat(CAT_OPTS);
// Mirrors the live marketplace grid sorts (fe-marketplace-next GRID_SORT_MAP) so a
// curated row can use the same orderings the storefront exposes. All values are
// honored by /search (OpenSearchProductSearchService sort switch).
// Sentinel for "no sort_by param", mirroring ALL_CAT: an endpoint without one is ordered by
// the BE's own default (`$options['sort_by'] ?: 'relevance'`), so the picker has to be able to
// show that state and re-emit it, rather than silently claiming Most viewed.
const NO_SORT = '__default__';
const SORT_OPTS = [
  { value: NO_SORT, label: 'Default (relevance)' },
  { value: 'most_view', label: 'Most viewed' },
  { value: 'product_desc', label: 'Newest' },
  { value: 'most_popular', label: 'Most popular' },
  { value: 'featured_item_score_desc', label: 'Featured' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
];

// BE truthy/falsy sets for boolean-ish /search params (FILTER_VALIDATE_BOOLEAN);
// values outside both sets parse to null and the BE ignores the param entirely.
const BE_TRUE = /^(true|1|on|yes)$/i;
const BE_FALSE = /^(false|0|off|no)$/i;
// /search params owned by the Search tab's controls; applySearch re-emits these and
// preserves everything else from the section's existing endpoint verbatim.
// NOTE: shipping and sneakers_condition are deliberately absent — the comma-list branch in
// applySearch consumes them before this check, and listing them here would read as the
// mechanism that drops them when it never runs for those names.
const MANAGED_SEARCH_PARAMS = ['availables', 'category', 'sort_by', 'under_retail'];
// Comma-list params (shipping, legacy sneakers_condition): BE urldecodes then splits
// on commas; the pre_verified token is the express filter, other tokens stand alone.
const listTokens = (v) => String(v == null ? '' : v).split(',').map((t) => t.trim()).filter(Boolean);
// PHP-style urldecode: '+' is a space, %xx decodes, malformed sequences stay raw.
const safeDecode = (s) => { const t = String(s).replace(/\+/g, ' '); try { return decodeURIComponent(t); } catch (e) { return t; } };
const WARN_TEXT_STYLE = { fontSize: '.78rem' };
// Append a value to an option list when it isn't already there, so react-select can render it.
const withStored = (opts, v) => (v && !opts.some((o) => o.value === v) ? opts.concat([{ value: v, label: `${v} (stored)` }]) : opts);

export default class SourceModal extends Component {
  constructor(props) {
    super(props);
    // Collections are path-scoped, so allCategory falls back to sneakers (matches the live
    // web homepage's /collections/sneakers). Search & rails support an explicit all-category
    // mode, so allCategory defaults to that instead.
    // props.category is the section's homepage_category, which app_subnav (and the orphan union in
    // HomepageBuilder's `cats`) can now set to any slug — but both defaults below feed BE
    // `category=` params, where an unknown value silently returns nothing (measured: a rail with
    // category=kids returns 0 items). So only a real product category is carried through; anything
    // else falls back exactly as 'allCategory' does. Reflection off the stored endpoint is
    // unaffected — sbCat still shows whatever the endpoint carries.
    const knownCat = CAT_OPTS.some((o) => o.value === props.category);
    this.apiCat = knownCat ? props.category : 'sneakers';
    const catDefault = knownCat ? props.category : ALL_CAT;
    // Reflect the section's saved /search endpoint in the Search tab, so reopening the
    // picker shows the stored filter instead of defaults (a ticked default checkbox used
    // to mask a stored availables=false). Exact-path check: /search/db is a different
    // backend and must not be reflected as if it were the plain /search.
    const ep = props.endpoint;
    const cur = typeof ep === 'string' && (ep === '/search' || ep.indexOf('/search?') === 0) ? ep : null;
    this.curSearch = cur; // applySearch merges over this, preserving unmanaged params
    const curQ = cur ? qs.parseUrl(cur).query : {};
    // Repeated keys: qs gives an array — take the LAST occurrence, matching PHP $_GET.
    const param = (name) => { const v = curQ[name]; return Array.isArray(v) ? v[v.length - 1] : v; };
    // Mirror BE parsing: boolean-ish params go through FILTER_VALIDATE_BOOLEAN
    // (true/1/on/yes), and express is a pre_verified token in the comma-listed
    // shipping or legacy sneakers_condition.
    const beTrue = (name) => BE_TRUE.test(param(name) || '');
    const hasPreVerified = (name) => listTokens(param(name)).some((t) => t.toLowerCase() === 'pre_verified');
    // A stored BE-false availables (false/0/off/no, or present-but-empty) means the
    // LIVE section currently shows ONLY sold-out items — flag it in the Search tab;
    // an absent param is merely "unfiltered" and an invalid value is ignored by BE.
    const availStored = param('availables');
    const availPresent = !!cur && Object.prototype.hasOwnProperty.call(curQ, 'availables');
    const sbCat = cur ? (param('category') || ALL_CAT) : catDefault;
    // A stored endpoint with no sort_by keeps that state (NO_SORT); only a NEW section defaults
    // to most_view, matching the other four controls which distinguish stored from absent.
    const sbSort = cur ? (param('sort_by') || NO_SORT) : 'most_view';
    // react-select 1.x resolves a string `value` by scanning `options` and falls back to the
    // placeholder when nothing matches, so a stored value outside our lists would render as an
    // EMPTY control — the same masking this reflection exists to remove. Offer it as its own
    // option instead. Kept per-tab: a category reflected off the stored /search endpoint must
    // not become a pickable RAIL category, since the rail tab applies on click with no Test.
    this.searchCatOpts = withStored(CAT_OPTS_WITH_ALL, sbCat);
    // No withStored here, and that is the point: catDefault is a known product category or
    // ALL_CAT, so it is always already in this list. The rail picker cannot grow a new value.
    this.railCatOpts = CAT_OPTS_WITH_ALL;
    this.sortOpts = withStored(SORT_OPTS, sbSort);
    this.state = {
      // A reflected search source opens on its own tab so the mirrored state is visible.
      tab: props.kind === 'data' ? (cur ? 'search' : 'collection') : props.kind,
      // Seed the Advanced box with the stored endpoint so a source this modal can't reflect
      // elsewhere (a custom /products or /collections path, or the excluded /search/db) is at
      // least visible and tweakable. Display-only: applyAdv is reachable only from the button
      // that renders after a passing Test, so nothing is applied until the editor tests it.
      adv: typeof props.endpoint === 'string' ? props.endpoint : '',
      test: null, testing: false,
      sbCat,
      sbSort,
      // checked = BE-truthy availables; absent (or the legacy false) loads unchecked.
      // New saves never write availables=false (see applySearch).
      sbAvail: cur ? beTrue('availables') : true,
      sbOosOnly: availPresent && (availStored == null || availStored === '' || BE_FALSE.test(availStored)),
      sbOosVal: availStored == null ? '' : `=${availStored}`, // exact stored form for the warning copy
      sbRetail: cur ? beTrue('under_retail') : false,
      sbExpress: cur ? (hasPreVerified('shipping') || hasPreVerified('sneakers_condition')) : false,
      railCat: catDefault,
      collections: [], collLoading: false, collError: null, collKeyword: '',
    };
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    // Only fetch collections when the Collection tab is actually visible — a reflected
    // search source opens on the Search tab and may never need the list (see openTab).
    if (this.props.kind === 'data' && this.state.tab === 'collection') this.fetchCollections();
  }

  openTab(tab) {
    this.setState({ tab, test: null });
    // Carry the typed keyword into the retry, or the list would come back unfiltered while
    // the search box still shows it — reading as "no matches" rather than "filter dropped".
    if (tab === 'collection' && this.props.kind === 'data' && !this._collFetched) {
      this.fetchCollections((this.state.collKeyword || '').trim() || undefined);
    }
  }

  componentWillUnmount() { this._mounted = false; }

  // Fetch real, current collections from the CMS endpoint (no more stale sample slugs).
  // A request sequence guards against stale responses: rapid searches won't let an
  // earlier (slower) response overwrite a later one.
  fetchCollections(keyword) {
    this._collFetched = true;
    const seq = (this._collSeq || 0) + 1;
    this._collSeq = seq;
    this.setState({ collLoading: true, collError: null });
    // active=1: only collections the public /collections/{category}/{slug} path can serve
    // (it requires an active collection). sort_by=updatedAt_desc → most-recently-updated first.
    const params = { per_page: 50, active: 1, sort_by: 'updatedAt_desc' };
    if (keyword) params.keyword = keyword;
    apiClient().get('/admins/collections', { params })
      .then((res) => {
        if (!this._mounted || seq !== this._collSeq) return; // stale: a newer search superseded this
        const rows = (((res.data || {}).data || {}).data) || [];
        // Capture each collection's own categories (+ which are primary). Collections are
        // multi-category and the /collections/{category}/{slug} path scopes products by
        // category, so the endpoint must use the COLLECTION's category, not the section's.
        const collections = rows.filter((r) => r && r.slug).map((r) => {
          const cats = (r.categories || []).map((c) => ({
            name: c && c.name,
            primary: !!(c && c.pivot && c.pivot.active),
          })).filter((c) => c.name);
          return { name: r.name || r.slug, slug: r.slug, cats };
        });
        this.setState({ collLoading: false, collections });
      })
      .catch((e) => {
        if (!this._mounted || seq !== this._collSeq) return;
        this._collFetched = false; // failed latest fetch: let the next tab activation retry
        this.setState({ collLoading: false, collError: (e && e.message) || 'Failed to load collections.' });
      });
  }

  apply(et, label, ep, slug) { this.props.onApply({ et, label, ep, slug }); }

  // Category segment for a collection's /collections/{category}/{slug} path. The public path
  // requires an ACTIVE category relation (pivot.active), so only consider active-pivot
  // categories: prefer the section's own if it's one, else the first active (any, as a
  // last resort if none are active; apiCat if no data).
  collCategory(c) {
    const active = (c.cats || []).filter((x) => x.primary).map((x) => x.name);
    const pool = active.length ? active : (c.cats || []).map((x) => x.name);
    if (!pool.length) return this.apiCat;
    if (pool.indexOf(this.apiCat) !== -1) return this.apiCat;
    return pool[0];
  }

  applySearch() {
    const { sbCat, sbSort, sbAvail, sbRetail, sbExpress } = this.state;
    // Merge over the section's existing /search endpoint: params this tab doesn't manage
    // (keyword, per_page, interval, page…) are preserved VERBATIM — no decode/re-encode
    // round-trip. shipping/sneakers_condition are comma-lists where the tab owns only
    // the pre_verified (express) token; their other tokens are carried over below.
    const rest = [];
    const kept = { shipping: [], sneakers_condition: [] };
    if (this.curSearch) {
      const qi = this.curSearch.indexOf('?');
      (qi === -1 ? [] : this.curSearch.slice(qi + 1).split('&').filter(Boolean)).forEach((pair) => {
        const eq = pair.indexOf('=');
        // PHP decodes param NAMES too — compare the decoded name so an encoded variant
        // (%73hipping=…) can't slip past the managed/list checks and override via last-wins.
        const name = safeDecode(eq === -1 ? pair : pair.slice(0, eq));
        if (name === 'shipping' || name === 'sneakers_condition') {
          const tokens = listTokens(safeDecode(eq === -1 ? '' : pair.slice(eq + 1)));
          // Assignment, not concat: PHP keeps only the LAST occurrence of a repeated key.
          kept[name] = tokens.filter((t) => t.toLowerCase() !== 'pre_verified');
          return;
        }
        if (MANAGED_SEARCH_PARAMS.indexOf(name) !== -1) return;
        rest.push(pair);
      });
    }
    const params = [];
    // Checked → availables=true (in-stock only). Unchecked → OMIT the param: sending
    // availables=false does not mean "unfiltered" — BE maps it to an out_of_stock=true
    // filter, i.e. ONLY sold-out items.
    if (sbAvail) params.push('availables=true');
    // "All categories" → omit the category filter so /search returns every category.
    // Reflected values were qs-decoded — re-encode so they round-trip byte-safely.
    if (sbCat !== ALL_CAT) params.push(`category=${encodeURIComponent(sbCat)}`);
    if (sbSort !== NO_SORT) params.push(`sort_by=${encodeURIComponent(sbSort)}`);
    if (sbRetail) params.push('under_retail=true');
    // Express joins the carried-over shipping tokens in ONE param — PHP keeps only the
    // last occurrence of a repeated key, so two shipping= params would drop one.
    const shipping = (sbExpress ? ['pre_verified'] : []).concat(kept.shipping);
    if (shipping.length) params.push(`shipping=${shipping.map(encodeURIComponent).join(',')}`);
    if (kept.sneakers_condition.length) params.push(`sneakers_condition=${kept.sneakers_condition.map(encodeURIComponent).join(',')}`);
    // Every control can be off with nothing carried over, so don't store a dangling `?`.
    const query = params.concat(rest).join('&');
    this.apply('search', `Search · ${sbCat === ALL_CAT ? 'all' : sbCat} ${sbSort === NO_SORT ? 'default' : sbSort}`, query ? `/search?${query}` : '/search');
  }

  async testEndpoint() {
    const ep = (this.state.adv || '').trim();
    // Endpoint must be a RELATIVE same-origin path, then allowlisted on its NORMALIZED path.
    // Two traps: (1) a traversal like /search/../admins/settings resolves to /admins/settings
    // when fetched; (2) an absolute/protocol-relative URL (https://attacker/search, //attacker
    // /search) has pathname '/search', so a pathname-only check passes it while the fetch hits
    // the foreign host. Guard both: require a single leading slash AND origin === base.
    const BASE = 'https://hpb-allowlist.invalid';
    let normEp = null;
    if (/^\/(?!\/)/.test(ep)) {
      try { const u = new URL(ep, BASE); if (u.origin === BASE) normEp = u.pathname + u.search; } catch (e) { /* unparseable */ }
    }
    if (normEp === null || !ENDPOINT_PREFIXES.some((p) => normEp.indexOf(p) === 0)) {
      this.setState({ test: { ok: false, msg: 'Rejected — must be a relative path starting with an allowed root.' } });
      return;
    }
    // Hit the endpoint and report the real item count. Stale-guard: if the input changed
    // mid-flight, ignore the result; tag the verdict with the tested `ep` so applyAdv()
    // applies the validated value, not whatever is currently typed.
    this.setState({ testing: true, test: null });
    const stale = () => (this.state.adv || '').trim() !== ep;
    try {
      const res = await apiClient().get(ep);
      if (!this._mounted || stale()) { if (this._mounted) this.setState({ testing: false }); return; }
      const body = res && res.data;
      const payload = body && body.data;
      let count = null;
      if (payload && Array.isArray(payload.data)) count = payload.data.length; // paginated list
      else if (payload && payload.products && Array.isArray(payload.products.data)) count = payload.products.data.length; // collection detail nests products under data.products.data
      else if (Array.isArray(payload)) count = payload.length;
      else if (body && typeof body.total === 'number') count = body.total;
      if (count === 0) this.setState({ testing: false, test: { ok: false, ep, msg: 'Valid endpoint but returns 0 items — section would be empty.' } });
      else this.setState({ testing: false, test: { ok: true, ep, msg: count != null ? `OK — returns ${count} items.` : 'OK — endpoint responded.' } });
    } catch (e) {
      if (!this._mounted || stale()) { if (this._mounted) this.setState({ testing: false }); return; }
      const status = e && e.response && e.response.status;
      this.setState({ testing: false, test: { ok: false, ep, msg: `Endpoint error${status ? ` (HTTP ${status})` : ''}: ${(e && e.message) || 'request failed'}.` } });
    }
  }

  applyAdv() {
    // Apply exactly what was tested, not the (possibly-changed) current input.
    const ep = (this.state.test && this.state.test.ep) || (this.state.adv || '').trim();
    // Classify by path so endpoint_type matches what the renderer expects. /settings is
    // a shortcut source (not search) — misclassifying it as 'search' would let it pass a
    // product-row's type check while the renderer ignores the /settings path.
    const et = ep.indexOf('/collections/') === 0 ? 'collection'
      : ep.indexOf('/brands/') === 0 ? 'brand'
        : ep.indexOf('/sliders') === 0 ? 'slider'
          : ep.indexOf('/settings') === 0 ? 'shortcut'
            : ep.indexOf('/products/') === 0 ? 'products' : 'search';
    // A collection row renders via the slug (web: endpoint_type === 'collection' && slug);
    // extract it from /collections/{category}/{slug}. Without a slug the row would silently
    // fall back to generic search, so reject it instead of applying a broken source.
    let slug;
    if (et === 'collection') {
      slug = ep.split('?')[0].split('/').filter(Boolean)[2]; // ['collections', category, slug]
      if (!slug) {
        this.setState({ test: { ok: false, ep, msg: 'Collection endpoint needs /collections/{category}/{slug} — slug is missing.' } });
        return;
      }
    }
    this.apply(et, `Custom · ${ep.split('?')[0]}`, ep, slug);
  }

  renderFixed() {
    const m = FIXED[this.props.kind];
    // FIXED is keyed by section kind, but two sources are context-scoped, so override here:
    // - shortcut: platform-specific setting key (web vs app homepage).
    // - slider: scoped to the section's category — /sliders filters by category name, so an
    //   app Sneakers tab must request category=sneakers, not a hardcoded all_category (which
    //   would save all-category sliders and never surface that tab's own). The "all"/web
    //   context maps to the all_category slider category.
    let endpoint = m[1];
    if (this.props.kind === 'shortcut') {
      endpoint = `/settings?name=shortcut_${this.props.platform === 'web' ? 'web' : 'app'}_kickavenue`;
    } else if (this.props.kind === 'slider') {
      // Deliberately NOT gated on knownCat like the product paths above: /sliders matches
      // `category.name` against its own, broader vocabulary — prod carries all_category,
      // the four product categories, and others (luxury, bca, kafair, Trading Cards…). The
      // product-category list would clamp those valid names to all_category. The cost is that
      // a homepage_category with no matching category row yields an empty slider set.
      const cat = this.props.category && this.props.category !== 'allCategory' ? this.props.category : ALL_CAT;
      endpoint = `/sliders?category=${cat}`;
    }
    return (
      <div>
        <p className="text-muted" style={{ fontSize: '.8rem' }}>This section's content is fixed to <b>{m[0]}</b>.</p>
        <ListGroup>
          <ListGroupItem action tag="button" type="button" onClick={() => this.apply(m[2], m[0], endpoint)}>
            Use {m[0]} <span className="badge badge-light float-right">{m[2]}</span>
          </ListGroupItem>
        </ListGroup>
      </div>
    );
  }

  render() {
    const { kind, onClose, title } = this.props;
    const s = this.state;
    const tabs = [['collection', 'Collection'], ['rail', 'Curated list'], ['search', 'Search filter'], ['advanced', 'Advanced']];
    return (
      <Modal isOpen toggle={onClose}>
        <ModalHeader toggle={onClose}>Content source{title ? ` — ${title}` : ''}</ModalHeader>
        <ModalBody>
          {kind !== 'data' ? this.renderFixed() : (
            <div>
              <Nav tabs className="mb-3">
                {tabs.map(([v, l]) => (
                  <NavItem key={v}>
                    <NavLink href="#" active={s.tab === v} onClick={(e) => { e.preventDefault(); this.openTab(v); }}>{l}</NavLink>
                  </NavItem>
                ))}
              </Nav>
              <TabContent activeTab={s.tab}>
                <TabPane tabId="collection">
                  <InputGroup size="sm" className="mb-2">
                    <Input bsSize="sm" placeholder="Search collections…" value={s.collKeyword}
                      onChange={(e) => this.setState({ collKeyword: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.fetchCollections(s.collKeyword.trim()); } }} />
                    <InputGroupAddon addonType="append">
                      <Button color="secondary" outline disabled={s.collLoading} onClick={() => this.fetchCollections(s.collKeyword.trim())}><i className="fa fa-search" /></Button>
                    </InputGroupAddon>
                  </InputGroup>
                  <div className="text-muted mb-2" style={{ fontSize: '.72rem' }}>Each collection uses its own category in the feed path (collections are category-scoped).</div>
                  {s.collLoading ? <div className="text-muted" style={{ fontSize: '.82rem' }}><Spinner size="sm" /> Loading collections…</div> : null}
                  {s.collError ? <Alert color="danger" className="py-2">{s.collError}</Alert> : null}
                  {!s.collLoading && !s.collError ? (
                    s.collections.length ? (
                      <ListGroup style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {s.collections.map((c) => {
                          const cat = this.collCategory(c);
                          return (
                            <ListGroupItem action tag="button" type="button" key={c.slug}
                              onClick={() => this.apply('collection', `Collection · ${c.name}`, `/collections/${cat}/${c.slug}?availables=TRUE&category=${cat}&per_page=12`, c.slug)}>
                              {c.name} <span className="badge badge-info float-right">{cat}</span>
                            </ListGroupItem>
                          );
                        })}
                      </ListGroup>
                    ) : <div className="text-muted" style={{ fontSize: '.82rem' }}>No collections found.</div>
                  ) : null}
                </TabPane>

                <TabPane tabId="rail">
                  <FormGroup>
                    <Label>Category <span className="text-muted" style={{ fontWeight: 400 }}>(applied to category-scoped rails)</span></Label>
                    <Select value={s.railCat} options={this.railCatOpts} clearable={false} onChange={(o) => o && this.setState({ railCat: o.value })} />
                  </FormGroup>
                  <ListGroup>
                    {/* On web, hide rails the Next renderer can't fetch as a ranked rail
                        (they'd fall through to generic search). The app honors all. */}
                    {RAILS.filter((rail) => this.props.platform === 'app' || rail.web !== false).map((rail) => (
                      <ListGroupItem action tag="button" type="button" key={rail.path}
                        onClick={() => this.apply('products', rail.cat ? `${rail.label} · ${s.railCat}` : rail.label, railEndpoint(rail, s.railCat))}>
                        {rail.label} <span className={`badge float-right ${rail.cat ? 'badge-info' : 'badge-light'}`}>{rail.cat ? s.railCat : 'global'}</span>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </TabPane>

                <TabPane tabId="search">
                  {s.sbOosOnly ? <Alert color="warning" className="py-1 mb-2" style={WARN_TEXT_STYLE}>Saved filter has <code>availables{s.sbOosVal}</code> — the live section currently shows <strong>only sold-out items</strong>. Applying replaces that: unchecked “In stock only” = no stock filter.</Alert> : null}
                  <FormGroup>
                    <Label>Category</Label>
                    <Select value={s.sbCat} options={this.searchCatOpts} clearable={false} onChange={(o) => o && this.setState({ sbCat: o.value })} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Sort by</Label>
                    <Select value={s.sbSort} options={this.sortOpts} clearable={false} onChange={(o) => o && this.setState({ sbSort: o.value })} />
                  </FormGroup>
                  <FormGroup check className="mb-1"><Label check><Input type="checkbox" checked={s.sbAvail} onChange={(e) => this.setState({ sbAvail: e.target.checked })} /> In stock only</Label></FormGroup>
                  <FormGroup check className="mb-1"><Label check><Input type="checkbox" checked={s.sbRetail} onChange={(e) => this.setState({ sbRetail: e.target.checked })} /> Under retail</Label></FormGroup>
                  <FormGroup check className="mb-2"><Label check><Input type="checkbox" checked={s.sbExpress} onChange={(e) => this.setState({ sbExpress: e.target.checked })} /> Express (pre-verified)</Label></FormGroup>
                  <Button color="primary" onClick={() => this.applySearch()}>Use this filter</Button>
                </TabPane>

                <TabPane tabId="advanced">
                  <p className="text-muted" style={{ fontSize: '.78rem' }}>Type a raw endpoint. Must start with an allowed root: <code>/search</code>, <code>/products/</code>, <code>/collections/</code>, <code>/brands/</code>, <code>/sliders</code>.</p>
                  <FormGroup>
                    <Input value={s.adv} placeholder="/products/top-selling?category=sneakers&range_type=week" onChange={(e) => this.setState({ adv: e.target.value, test: null })} />
                  </FormGroup>
                  <Button color="secondary" outline disabled={s.testing} onClick={() => this.testEndpoint()}><i className={`fa ${s.testing ? 'fa-spinner fa-spin' : 'fa-flask'}`} /> {s.testing ? 'Testing…' : 'Test'}</Button>
                  {s.test ? <Alert color={s.test.ok ? 'success' : 'danger'} className="mt-2 mb-2 py-2">{s.test.msg}</Alert> : null}
                  {s.test && s.test.ok ? <Button color="primary" className="mt-1" onClick={() => this.applyAdv()}>Use endpoint</Button> : null}
                </TabPane>
              </TabContent>
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  }
}
